import { useCallback, useMemo } from 'react'
import {
  addSprintToRowWithSpi,
  isAssociatedWithSomeSprintBySpi,
  isAssociatedWithTheSprintBySpi,
  isRowAssociatedWithTheSprint,
  isProductBacklogItemRow,
  ProductBacklogItemRow,
  ProductBacklogRow,
  setSprintOfRowWithSpi,
  isUnsetSprintRowUuid,
  removeSprintProductItemOfRow,
  SprintProductItemDetail,
  getMaxDisplayOrderInSprint,
} from '../productBacklog'
import { moveRowsToLastChild } from '../../../containers/BulkSheetView/hooks/actions/moveTreeRows'
import { SprintDetail } from '../../../../lib/functions/sprint'
import { useDispatch } from 'react-redux'
import { requireSave } from '../../../../store/requiredSaveData'
import {
  insertRowsAfterSprint,
  removeRowsFromData,
  createUnsetSprintPbiRow,
  insertCopiedRowForMultiSprint,
  insertCopiedRowForDuplicateInSameSprint,
  sortPbiRowsWithinSprints,
} from './sprintOperations'

/**
 * 既に移動先スプリントに紐付いている行を移動する
 * 既存の未使用SPIを優先的に再利用し、足りない場合のみ新規SPIを作成
 */
const moveRowsAlreadyInTargetSprint = (
  data: ProductBacklogRow[],
  rowsToMove: ProductBacklogItemRow[],
  targetSprintUuid: string,
  targetSprint: SprintDetail,
  currentSprintUuid?: string
): ProductBacklogRow[] => {
  const rowsToInsert: ProductBacklogItemRow[] = []
  const rowUuidsToRemove = new Set<string>()

  // 移動先スプリントの既存SPIを取得
  const targetSprintSpis = (rowsToMove[0].sprintProductItems || []).filter(
    spi => spi.sprintUuid === targetSprintUuid
  )

  // 既にそのSPIを使っている行のUUIDを取得
  const usedSpiUuids = new Set(
    data
      .filter(
        r =>
          isProductBacklogItemRow(r) &&
          r.treeValue[0] === targetSprintUuid &&
          r.originalUuid === rowsToMove[0].originalUuid
      )
      .map(r => r.uuid)
  )

  // 未使用のSPIを取得
  const unusedSpis = targetSprintSpis.filter(spi => !usedSpiUuids.has(spi.uuid))

  let unusedSpiIndex = 0
  // 新規SPI作成時のdisplayOrder（スプリント内の末尾に追加）
  let nextDisplayOrder = getMaxDisplayOrderInSprint(data, targetSprintUuid) + 1

  for (const row of rowsToMove) {
    rowUuidsToRemove.add(row.uuid)

    // 元のスプリントからSPIを削除
    if (currentSprintUuid && !isUnsetSprintRowUuid(currentSprintUuid)) {
      removeSprintProductItemOfRow(row, row.uuid, currentSprintUuid)
    }

    let targetSpi: SprintProductItemDetail | undefined

    if (unusedSpiIndex < unusedSpis.length) {
      // 未使用のSPIを再利用
      targetSpi = unusedSpis[unusedSpiIndex]
      unusedSpiIndex++
    } else {
      // 足りない場合のみ新しいSPIを作成（スプリント末尾に追加）
      targetSpi = addSprintToRowWithSpi(
        row,
        targetSprint,
        true,
        nextDisplayOrder
      )
      nextDisplayOrder++
    }

    if (targetSpi) {
      const movedRow: ProductBacklogItemRow = {
        ...row,
        uuid: targetSpi.uuid,
        treeValue: [targetSprintUuid, targetSpi.uuid],
        edited: true,
      }
      rowsToInsert.push(movedRow)
    }
  }

  // 元の行を削除して新しい行を挿入
  let newData = data.filter(r => !rowUuidsToRemove.has(r.uuid))
  newData = insertRowsAfterSprint(newData, targetSprintUuid, rowsToInsert)

  return newData
}

/**
 * スプリント操作を行うReact Hook
 *
 * 責務:
 * - React状態管理（useCallback, useMemo, useDispatch）
 * - UI層からの呼び出しインターフェース提供
 *
 * ビジネスロジックはドメイン層に委譲:
 * - 判定関数: domain/value-object/SprintAssignment
 * - 操作関数: domain/service/SprintAssignmentService
 * - 行モデル操作: productBacklog.ts
 */

/**
 * PBIにスプリントを設定する
 */
export type SetSprint = (
  sprintUuid: string,
  pbiRows: ProductBacklogItemRow[],
  currentSprintUuid?: string
) => void
/**
 * PBIのスプリントをドラッグ＆ドロップで付け替える
 */
export type SwitchSprint = (
  sprintUuid: string,
  pbiRows: ProductBacklogItemRow[]
) => void
export type RemoveFromParentSprintRow = (
  pbiRows: ProductBacklogItemRow[]
) => void
export type AddSprint = (
  sprintUuid: string,
  pbiRows: ProductBacklogItemRow[]
) => void
export type DuplicateInSameSprint = (pbiRow: ProductBacklogItemRow) => void

export const useSprintManager = (
  data: ProductBacklogRow[],
  setData: (data: ProductBacklogRow[]) => void,
  sprints: SprintDetail[]
): {
  setSprint: SetSprint
  switchSprint: SwitchSprint
  removeFromParentSprintRow: RemoveFromParentSprintRow
  addSprint: AddSprint
  duplicateInSameSprint: DuplicateInSameSprint
} => {
  const dispatch = useDispatch()
  const sprintMapByUuid = useMemo(() => {
    const map: { [uuid: string]: SprintDetail } = {}
    sprints.forEach(sprint => {
      map[sprint.uuid] = sprint
    })
    return map
  }, [sprints])

  const setSprint = useCallback(
    (
      sprintUuid: string,
      pbiRows: ProductBacklogItemRow[],
      currentSprintUuid?: string
    ) => {
      const targetSprint = sprintMapByUuid[sprintUuid]
      if (!targetSprint) return

      // 移動対象と、既に移動先スプリントに紐付いている行を分類
      const pbiRowUuidsToMove = new Set<string>()
      const pbiRowsAlreadyInSprint: ProductBacklogItemRow[] = []
      pbiRows.forEach(row => {
        if (isAssociatedWithTheSprintBySpi(row, sprintUuid)) {
          pbiRowsAlreadyInSprint.push(row)
        } else {
          pbiRowUuidsToMove.add(row.uuid)
        }
      })

      if (pbiRowUuidsToMove.size === 0 && pbiRowsAlreadyInSprint.length === 0) {
        return
      }

      const isFromUnsetSprint =
        currentSprintUuid && isUnsetSprintRowUuid(currentSprintUuid)

      let newData = [...data]
      // 新規SPI作成時のdisplayOrder（スプリント内の末尾に追加）
      let nextDisplayOrder = getMaxDisplayOrderInSprint(newData, sprintUuid) + 1

      // 通常の移動処理（移動先スプリントに紐付いていない行）
      if (pbiRowUuidsToMove.size > 0) {
        const { newData: dataWithoutRows, removedRows } = removeRowsFromData(
          newData,
          pbiRowUuidsToMove,
          row => {
            if (
              currentSprintUuid &&
              !isFromUnsetSprint &&
              isAssociatedWithSomeSprintBySpi(row)
            ) {
              // この行のSPIのみを削除して、新しいスプリントを追加
              removeSprintProductItemOfRow(row, row.uuid, currentSprintUuid)
              // forceNew=trueで常に新しいSPIを作成（複数行移動時に各行が別のSPIを持つ必要があるため）
              const newSpi = addSprintToRowWithSpi(
                row,
                targetSprint,
                true,
                nextDisplayOrder
              )
              nextDisplayOrder++
              if (newSpi) {
                row.uuid = newSpi.uuid
              }
              row.treeValue = [sprintUuid, row.uuid]
            } else {
              setSprintOfRowWithSpi(row, targetSprint)
            }
            return row
          }
        )
        newData = insertRowsAfterSprint(
          dataWithoutRows,
          sprintUuid,
          removedRows
        )
      }

      // 既に移動先スプリントに紐付いている行の移動処理
      // 既存の未使用SPIを優先的に再利用し、足りない場合のみ新規SPIを作成
      if (pbiRowsAlreadyInSprint.length > 0) {
        newData = moveRowsAlreadyInTargetSprint(
          newData,
          pbiRowsAlreadyInSprint,
          sprintUuid,
          targetSprint,
          currentSprintUuid
        )
      }

      setData(newData)
      dispatch(requireSave())
    },
    [sprintMapByUuid, data, setData, dispatch]
  )

  const switchSprint = useCallback(
    (sprintUuid: string, pbiRows: ProductBacklogItemRow[]) => {
      const targetSprint = sprintMapByUuid[sprintUuid]

      // 早期リターン: 対象スプリントが存在しない場合（UNSET_SPRINT以外）
      if (!targetSprint && !isUnsetSprintRowUuid(sprintUuid)) {
        return
      }

      // Exclude rows already associated with the target sprint.
      // 行単位で判定（共有配列でも正しく動作するよう、成果物単位ではなく行のSPIで判定）
      const pbiRowsToMove: ProductBacklogItemRow[] = []
      const fromSprintRowUuidMapByPbiRow: { [uuid: string]: string } = {}
      pbiRows.forEach(v => {
        if (!isRowAssociatedWithTheSprint(v, sprintUuid)) {
          pbiRowsToMove.push(v)
          fromSprintRowUuidMapByPbiRow[v.uuid] = v.treeValue[0]
        }
      })
      if (pbiRowsToMove.length === 0) return
      const pbiRowUuidsToMove = new Set(pbiRowsToMove.map(r => r.uuid))

      let newData = moveRowsToLastChild(data, pbiRowsToMove, sprintUuid)
      // 新規SPI作成時のdisplayOrder（スプリント内の末尾に追加）
      let nextDisplayOrder = getMaxDisplayOrderInSprint(newData, sprintUuid) + 1

      // newData内の該当行を直接修正する（moveRowsToLastChildが新しいオブジェクトを作成するため）
      pbiRowUuidsToMove.forEach(rowUuid => {
        const rowInNewData = newData.find(
          r => isProductBacklogItemRow(r) && r.uuid === rowUuid
        ) as ProductBacklogItemRow | undefined
        if (!rowInNewData) {
          return
        }

        const fromSprintRowUuid = fromSprintRowUuidMapByPbiRow[rowUuid]

        // 移動元がスプリント未設定でない場合、移動する行のSPIを削除
        if (!isUnsetSprintRowUuid(fromSprintRowUuid)) {
          // この行自身のSPIを削除（行UUIDで特定）
          removeSprintProductItemOfRow(rowInNewData, rowUuid, fromSprintRowUuid)
        }

        if (isUnsetSprintRowUuid(sprintUuid)) {
          setSprintOfRowWithSpi(rowInNewData, undefined)
        } else if (targetSprint) {
          // SPIとwbsItem.sprintsの両方を確実に更新
          // forceNew=trueで常に新規SPIを作成（同一スプリントに既存SPIがあっても新規作成）
          const newSpi = addSprintToRowWithSpi(
            rowInNewData,
            targetSprint,
            true,
            nextDisplayOrder
          )
          nextDisplayOrder++
          // 行UUIDを新しいSPIのUUIDに更新
          if (newSpi) {
            rowInNewData.uuid = newSpi.uuid
            rowInNewData.treeValue = [sprintUuid, newSpi.uuid]
          }
        }
      })

      setData(newData)
      dispatch(requireSave())
    },
    [sprintMapByUuid, data, setData, dispatch]
  )

  const removeFromParentSprintRow = useCallback(
    (pbiRows: ProductBacklogItemRow[]) => {
      const pbiRowUuids: string[] = []
      const editedPbiRowMapByOriginalUuid: {
        [uuid: string]: ProductBacklogItemRow
      } = {}

      // 1. まず全ての行からSPIを削除
      pbiRows.forEach(pbiRow => {
        const parentSprintRowUuid = pbiRow.treeValue[0]
        const parentSprint = sprintMapByUuid[parentSprintRowUuid]
        if (!parentSprint) return
        pbiRowUuids.push(pbiRow.uuid)

        removeSprintProductItemOfRow(pbiRow, pbiRow.uuid, parentSprint.uuid)
        editedPbiRowMapByOriginalUuid[pbiRow.originalUuid] = pbiRow
      })

      // 2. 全ての削除処理が終わった後、スプリント未設定行を作成するか判定
      // 共有配列なので、全ての削除が終わった時点でsprintProductItemsが空かどうかで判定
      const pbiRowsNotAssociatedWithSprint: ProductBacklogItemRow[] = []
      const processedOriginalUuids = new Set<string>()
      pbiRows.forEach(pbiRow => {
        // 同じoriginalUuidは1回だけ処理
        if (processedOriginalUuids.has(pbiRow.originalUuid)) return
        processedOriginalUuids.add(pbiRow.originalUuid)

        // sprintProductItemsが空になった場合のみスプリント未設定行を作成
        if (pbiRow.sprintProductItems.length === 0) {
          pbiRowsNotAssociatedWithSprint.push(createUnsetSprintPbiRow(pbiRow))
        }
      })

      const newData = data.filter(v => {
        if (
          isProductBacklogItemRow(v) &&
          editedPbiRowMapByOriginalUuid[v.originalUuid]
        ) {
          const editedPbiRow = editedPbiRowMapByOriginalUuid[v.originalUuid]
          v.edited = editedPbiRow.edited
          v.editedData = editedPbiRow.editedData
          v.sprintProductItems = editedPbiRow.sprintProductItems
        }
        return !pbiRowUuids.includes(v.uuid)
      })
      newData.push(...pbiRowsNotAssociatedWithSprint)

      setData(newData)
      dispatch(requireSave())
    },
    [data, setData, dispatch, sprintMapByUuid]
  )

  const addSprint = useCallback(
    (sprintUuid: string, pbiRows: ProductBacklogItemRow[]) => {
      const targetSprint = sprintMapByUuid[sprintUuid]
      if (!targetSprint) return

      const targetPbiRowsBeforeSetSprint: ProductBacklogItemRow[] = []
      const targetPbiRowsBeforeUnsetSprint: ProductBacklogItemRow[] = []
      const targetPbiRowsAlreadyInSprint: ProductBacklogItemRow[] = []

      // 新規SPI作成時のdisplayOrder（スプリント内の末尾に追加）
      let nextDisplayOrder = getMaxDisplayOrderInSprint(data, sprintUuid) + 1

      pbiRows.forEach(v => {
        if (isAssociatedWithTheSprintBySpi(v, sprintUuid)) {
          // 既に紐付いているスプリントの場合は、同一スプリント内で行を追加
          targetPbiRowsAlreadyInSprint.push(v)
          return
        }
        if (isAssociatedWithSomeSprintBySpi(v)) {
          targetPbiRowsBeforeSetSprint.push(v)
        } else {
          targetPbiRowsBeforeUnsetSprint.push(v)
        }
        addSprintToRowWithSpi(v, targetSprint, false, nextDisplayOrder)
        nextDisplayOrder++
      })

      let newData =
        targetPbiRowsBeforeUnsetSprint.length > 0
          ? moveRowsToLastChild(
              data,
              targetPbiRowsBeforeUnsetSprint,
              sprintUuid
            )
          : [...data]

      // 既にスプリントに紐付いている行のコピーを追加（別スプリントから追加の場合）
      for (const row of targetPbiRowsBeforeSetSprint) {
        const result = insertCopiedRowForMultiSprint(newData, sprintUuid, row)
        newData = result.newData
      }

      // 同一スプリント内で行を追加（既に紐付いているスプリントを選択した場合）
      for (const row of targetPbiRowsAlreadyInSprint) {
        const result = insertCopiedRowForDuplicateInSameSprint(
          newData,
          sprintUuid,
          row
        )
        newData = result.newData
      }

      // スプリント内の行をdisplayOrderでソート
      newData = sortPbiRowsWithinSprints(newData)

      setData(newData)
      dispatch(requireSave())
    },
    [sprintMapByUuid, data, setData, dispatch]
  )

  const duplicateInSameSprint = useCallback(
    (pbiRow: ProductBacklogItemRow) => {
      const parentSprintUuid = pbiRow.treeValue[0]
      if (!parentSprintUuid || isUnsetSprintRowUuid(parentSprintUuid)) return

      // 元の行をコピーして新規行を作成（同一スプリント内複製用）
      // 値のコピーはUI側（copyProductBacklogItemRowForDuplicateInSameSprint）で完結
      const result = insertCopiedRowForDuplicateInSameSprint(
        data,
        parentSprintUuid,
        pbiRow
      )
      if (!result.copiedRow) return

      setData(result.newData)
      dispatch(requireSave())
    },
    [data, setData, dispatch]
  )

  return {
    setSprint,
    switchSprint,
    removeFromParentSprintRow,
    addSprint,
    duplicateInSameSprint,
  }
}
